import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { rotatedTokens, signedOut } from '@/features/auth/authSlice';
import type { RootState } from '@/store';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api',
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) headers.set('authorization', `Bearer ${token}`);
    return headers;
  },
});

/**
 * Wraps the base query with refresh-on-401 behavior:
 *
 *   1. If the response is 401 AND we have a refresh token, fire
 *      `POST /auth/refresh` exactly once (concurrent 401s share the same
 *      promise so we don't burn the token's single-use rotation).
 *   2. On success, persist the new tokens via `rotatedTokens` and retry the
 *      original request.
 *   3. On failure, dispatch `signedOut` (the refresh token is gone) so the
 *      app routes back to /login. The original 401 surfaces to the caller.
 */
let inflightRefresh: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) return result;

  const state = api.getState() as RootState;
  const refreshToken = state.auth.refreshToken;
  if (!refreshToken) return result;

  // Don't try to refresh the refresh endpoint itself.
  const url = typeof args === 'string' ? args : args.url;
  if (url === '/auth/refresh' || url === '/auth/logout') return result;

  if (!inflightRefresh) {
    inflightRefresh = (async () => {
      const refreshRes = await rawBaseQuery(
        { url: '/auth/refresh', method: 'POST', body: { refreshToken } },
        api,
        extraOptions,
      );
      if (refreshRes.error || !refreshRes.data) return null;
      const data = refreshRes.data as { accessToken: string; refreshToken: string };
      return { accessToken: data.accessToken, refreshToken: data.refreshToken };
    })().finally(() => {
      // Allow the next 401 (after this rotation expires too) to trigger again.
      setTimeout(() => {
        inflightRefresh = null;
      }, 0);
    });
  }

  const rotated = await inflightRefresh;
  if (!rotated) {
    api.dispatch(signedOut());
    return result;
  }

  api.dispatch(rotatedTokens(rotated));
  // Retry the original request — `prepareHeaders` reads the new access token
  // from Redux state directly.
  result = await rawBaseQuery(args, api, extraOptions);
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Me',
    'Template',
    'TemplateVersion',
    'Gallery',
    'Provider',
    'Tokens',
    'Automation',
    'Send',
    'Experiment',
    'Webhook',
    'ApiKey',
    'Sender',
    'Member',
    'Invitation',
    'TrackingDomain',
    'FeatureFlags',
    'Integration',
    'Approval',
    'Nps',
    'Snippet',
    'Asset',
  ],
  endpoints: () => ({}),
});
